import {useMemo} from 'react';

import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {
  hasGrantedTools,
  MCP_SIDEPANEL_QUERY,
  MCPServerConfig,
  MCPServerUserConfig,
} from 'llm/chat-library/sidepanel/AISettingsConnectors';

import type {AIConnectorsQuery} from '../sidepanel/__generated/AIConnectorsQuery';

export interface UseConnectorsResult {
  /** Connectors the current user has already connected. */
  connectedConfigs: Array<MCPServerUserConfig>;
  /** Installed connectors with admin-granted tools that the user can connect to. */
  availableConfigs: Array<MCPServerConfig>;
  /** Installed connectors that still need an admin to grant tool access before a user can connect. */
  awaitingSetupConfigs: Array<MCPServerConfig>;
  isLoading: boolean;
  isErrored: boolean;
  /** Reload the connector lists, e.g. after a connect/disconnect. */
  refetch: () => void;
}

/**
 * Loads the AI Connectors listing and splits it into the three groups the Connectors tab shows:
 * Connected, Available, and Awaiting setup. Mirrors the grouping the legacy AISettingsConnectors
 * view derives, reusing its shared query and helpers so both surfaces stay in sync.
 */
export default function useConnectors(): UseConnectorsResult {
  const queryResult = useBenchlingQuery<AIConnectorsQuery>(MCP_SIDEPANEL_QUERY, {
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
  });

  const grouped = useMemo(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return {
        connectedConfigs: [] as Array<MCPServerUserConfig>,
        availableConfigs: [] as Array<MCPServerConfig>,
        awaitingSetupConfigs: [] as Array<MCPServerConfig>,
      };
    }
    const connectedConfigs = queryResult.data.mcpServerUserConfigs;
    const connectedServerIds = new Set(connectedConfigs.map((uc) => uc.mcpServerConfig.id));
    const notConnected = queryResult.data.mcpServerConfigs.filter((sc) => !connectedServerIds.has(sc.id));
    return {
      connectedConfigs,
      availableConfigs: notConnected.filter((sc) => hasGrantedTools(sc)),
      awaitingSetupConfigs: notConnected.filter((sc) => !hasGrantedTools(sc)),
    };
  }, [queryResult]);

  return {
    ...grouped,
    isLoading: queryResult.type === QueryResultType.PENDING,
    isErrored: queryResult.type === QueryResultType.ERROR,
    refetch: queryResult.refetch,
  };
}
